import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { ToastStack } from "../components/ToastStack";
import { continuarConPlanPendiente } from "../lib/planPendiente";

function Avatar({ url, nombre, size = 88 }: { url: string | null; nombre: string; size?: number }) {
  const initial = (nombre || "U")[0]?.toUpperCase() ?? "U";
  if (url) {
    return (
      <img src={url} alt={nombre}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "linear-gradient(135deg, #C99A6E, #9CB89F)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 800, color: "#fff", fontFamily: "var(--font-display)",
    }}>
      {initial}
    </div>
  );
}

export default function CompletarPerfil() {
  const navigate = useNavigate();
  const { token, user, updateUser } = useAuth();
  const { toasts, showToast, dismiss } = useToast();

  const [fotoUrl, setFotoUrl]   = useState<string | null>(null);
  const [nombre, setNombre]     = useState("");
  const [pwNueva, setPwNueva]   = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [saving, setSaving]     = useState(false);
  const [omitiendo, setOmitiendo] = useState(false);

  useEffect(() => {
    setFotoUrl(user?.foto_url ?? user?.avatar_url ?? null);
    setNombre(user?.nombre ?? "");
  }, [user]);

  async function finalizar() {
    if (!token) return;
    const fueAStripe = await continuarConPlanPendiente(token);
    if (!fueAStripe) navigate("/dashboard", { replace: true });
  }

  async function handleFotoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const input = e.target;
    if (!file || !token) return;

    if (file.size > 4 * 1024 * 1024) {
      showToast("La foto no puede pesar más de 4 MB", "error");
      input.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      showToast("No se pudo leer el archivo seleccionado", "error");
      input.value = "";
    };
    reader.onload = async () => {
      try {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(",")[1];
        const r = await api.subirFotoPerfil({ foto_base64: base64, mime_type: file.type }, token);
        if (r.ok) {
          setFotoUrl(r.data.foto_url);
          updateUser({ foto_url: r.data.foto_url });
        } else {
          showToast(r.error.message, "error");
        }
      } catch {
        showToast("No se pudo subir la foto. Intenta de nuevo.", "error");
      } finally {
        input.value = "";
      }
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;

    if (pwNueva.length < 8) {
      showToast("La contraseña debe tener al menos 8 caracteres", "error");
      return;
    }
    if (pwNueva !== pwConfirm) {
      showToast("Las contraseñas no coinciden", "error");
      return;
    }

    setSaving(true);
    const [rPerfil, rPassword] = await Promise.all([
      api.updatePerfil({ nombre: nombre.trim() }, token),
      api.cambiarPassword({ password_actual: "", password_nueva: pwNueva }, token),
    ]);
    setSaving(false);

    if (!rPerfil.ok) { showToast(rPerfil.error.message, "error"); return; }
    if (!rPassword.ok) { showToast(rPassword.error.message, "error"); return; }

    await finalizar();
  }

  async function handleOmitir() {
    setOmitiendo(true);
    await finalizar();
  }

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg)", fontFamily: "var(--font-body)", padding: 24 }}>
      <div className="card" style={{ maxWidth: 440, width: "100%", padding: 36 }}>
        <div style={{ textAlign: "center", marginBottom: 26 }}>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 24, letterSpacing: "-0.02em", margin: "0 0 6px" }}>
            Completa tu perfil
          </h1>
          <p style={{ fontSize: 14, color: "var(--muted)", margin: 0 }}>
            Personaliza tu cuenta antes de empezar
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 28 }}>
          <Avatar url={fotoUrl} nombre={nombre} />
          <label className="btn btn-outline btn--sm" style={{ marginTop: 12, cursor: "pointer" }}>
            Cambiar foto
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFotoChange} />
          </label>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="field-label">Nombre</label>
          <input className="form-input" value={nombre} onChange={(e) => setNombre(e.target.value)}
            placeholder="Tu nombre" maxLength={100} style={{ marginBottom: 18 }} />

          <label className="field-label">Contraseña</label>
          <input type="password" className="form-input" value={pwNueva} onChange={(e) => setPwNueva(e.target.value)}
            placeholder="Mínimo 8 caracteres" minLength={8} style={{ marginBottom: 18 }} />

          <label className="field-label">Confirmar contraseña</label>
          <input type="password" className="form-input" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
            placeholder="Repite la contraseña" minLength={8} style={{ marginBottom: 24 }} />

          <button type="submit" className="btn btn-primary btn--full" disabled={saving || omitiendo}>
            {saving ? <><span className="spinner" /> Guardando…</> : "Continuar"}
          </button>
        </form>

        <button
          type="button"
          onClick={handleOmitir}
          disabled={saving || omitiendo}
          style={{
            display: "block", margin: "18px auto 0", background: "none", border: "none",
            cursor: omitiendo ? "default" : "pointer", fontSize: 13.5, color: "var(--muted-2)", textDecoration: "underline",
          }}
        >
          {omitiendo ? "Redirigiendo…" : "Omitir por ahora"}
        </button>
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
